import { Button, Col, Flex, Form, Input, InputNumber, Row } from "antd";
import { isEmpty } from "lodash-es";
import { useEffect } from "react";
import { useIntl } from "react-intl";
import { Link, useParams } from "react-router-dom";
import type { InfinitySelectOption } from "~components/InfinitySelect/InfinitySelect.tsx";
import { SelectStage } from "~components/SelectStage/SelectStage.tsx";
import { formItemSharedStyles } from "~constants/sharedStyles.ts";
import { PermissionNames, PermissionOperations } from "~enums/permissions.ts";
import { useGetSeasonPool } from "~pages/SeasonPools/hooks/useGetSeasonPool.ts";
import { useSeasonPoolService } from "~pages/SeasonPools/hooks/useSeasonPoolService.ts";
import Page from "../../../Common/Page";
import LoaderContainer from "../../../Common/Page/LoaderContainer";
import PermissionCheck from "../../../Common/PermissionCheck";

const SeasonPoolManagePage = () => {
  const { data: seasonPool, isLoading } = useGetSeasonPool();
  const { customerId, sport, organizationId, seasonId } = useParams();
  const { formatMessage } = useIntl();
  const [form] = Form.useForm();
  const { updateSeasonPool, isUpdatePending: isUpdatingSeasonPool } = useSeasonPoolService();
  const pageTitle = seasonPool != null ? seasonPool.nameLocal : formatMessage({ id: "loading" });

  useEffect(() => {
    if (!seasonPool) {
      return;
    }

    const stageValue: InfinitySelectOption | null = isEmpty(seasonPool.stageCode)
      ? null
      : {
          label: (seasonPool.stageName ?? seasonPool.stage?.nameLocal ?? seasonPool.stageCode) || "",
          value: seasonPool.stageCode || "",
        };

    form.setFieldsValue({
      nameLocal: seasonPool.nameLocal,
      nameLatin: seasonPool.nameLatin ?? null,
      abbreviationLocal: seasonPool.abbreviationLocal ?? null,
      abbreviationLatin: seasonPool.abbreviationLatin ?? null,
      externalId: seasonPool.externalId ?? null,
      poolOrder: seasonPool.poolOrder ?? null,
      stageCode: stageValue,
    });
  }, [seasonPool, form]);

  const onUpdateButtonClick = async () => {
    if (!seasonPool?.poolCode) {
      return;
    }

    const formValues = (await form.validateFields()) ?? {};

    await updateSeasonPool(seasonPool.poolCode, {
      nameLocal: formValues.nameLocal,
      nameLatin: formValues.nameLatin || null,
      abbreviationLocal: formValues.abbreviationLocal || null,
      abbreviationLatin: formValues.abbreviationLatin || null,
      externalId: formValues.externalId || null,
      poolOrder: formValues.poolOrder ?? null,
      stageCode: formValues.stageCode?.value ? formValues.stageCode.value : null,
    });
  };

  return (
    <Page title={pageTitle}>
      <LoaderContainer loading={isLoading}>
        <Row>
          <Col span={12}>
            <Flex vertical gap="middle">
              <Form form={form} labelCol={{ span: 8 }} wrapperCol={{ span: 16 }}>
                <Form.Item
                  name="nameLocal"
                  label={formatMessage({ id: "name.local" })}
                  tooltip={formatMessage({ id: "form.help.local.name.item" }, { item: formatMessage({ id: "pool" }) })}
                  style={formItemSharedStyles}
                  rules={[{ required: true }]}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name="abbreviationLocal"
                  label={formatMessage({ id: "name.local.abbreviation" })}
                  tooltip={formatMessage({ id: "form.help.local.abbreviation" })}
                  style={formItemSharedStyles}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name="nameLatin"
                  label={formatMessage({ id: "name.latin" })}
                  tooltip={formatMessage({ id: "form.help.latin.name.item" }, { item: formatMessage({ id: "pool" }) })}
                  style={formItemSharedStyles}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name="abbreviationLatin"
                  label={formatMessage({ id: "name.latin.abbreviation" })}
                  tooltip={formatMessage({ id: "form.help.latin.abbreviation" })}
                  style={formItemSharedStyles}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name="externalId"
                  label={formatMessage({ id: "externalId" })}
                  tooltip={formatMessage({ id: "externalId" })}
                  style={formItemSharedStyles}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name="poolOrder"
                  label={formatMessage({ id: "pool.order" })}
                  tooltip={formatMessage(
                    { id: "form.help.order.different.within.itemA.itemB" },
                    {
                      itemA: formatMessage({ id: "pools" }),
                      itemB: formatMessage({ id: "season" }),
                    },
                  )}
                  style={formItemSharedStyles}
                  rules={[{ required: true }]}
                >
                  <InputNumber min={0} style={{ width: "100%" }} />
                </Form.Item>
                <Form.Item
                  name="stageCode"
                  label={formatMessage({ id: "stage" })}
                  tooltip={formatMessage({ id: "form.help.pool.stage" })}
                  style={formItemSharedStyles}
                >
                  <SelectStage />
                </Form.Item>
              </Form>
              <Flex justify="flex-end" gap="large">
                <PermissionCheck permissionKeys={[PermissionNames.POOL]} permissionOptions={[PermissionOperations.UPDATE]}>
                  <Button
                    type="primary"
                    onClick={() => void onUpdateButtonClick()}
                    disabled={isUpdatingSeasonPool || !seasonPool?.poolCode}
                  >
                    {formatMessage({ id: "pool.update" })}
                  </Button>
                </PermissionCheck>
                <Link to={`/${customerId}/admin/${sport}/${organizationId}/seasons/${seasonId}/pools`}>
                  <Button type="primary">{formatMessage({ id: "close" })}</Button>
                </Link>
              </Flex>
            </Flex>
          </Col>
        </Row>
      </LoaderContainer>
    </Page>
  );
};

export default SeasonPoolManagePage;
